import { defaultLocale, isLocale } from "@saas/internationalization";
import { createBrowserLogger } from "@saas/logger";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, StrictMode, Suspense } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router";
import { LandingPage } from "./LandingPage";
import "./styles.css";
import "./i18n";

const logger = createBrowserLogger({
  environment: import.meta.env.MODE ?? "browser",
  release: import.meta.env.VITE_APP_VERSION ?? "unknown",
});
window.addEventListener("error", () => {
  logger.emit({
    event: "web.runtime.failed",
    operation: "render",
    outcome: "failure",
    errorClass: "WindowError",
  });
});
window.addEventListener("unhandledrejection", () => {
  logger.emit({
    event: "web.promise.failed",
    operation: "async-operation",
    outcome: "failure",
    errorClass: "UnhandledRejection",
  });
});

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});
const AuthPage = lazy(() =>
  import("./AuthPage").then((module) => ({ default: module.AuthPage })),
);
const AppPortal = lazy(() =>
  import("./AppPortal").then((module) => ({ default: module.AppPortal })),
);
const AdminPortal = lazy(() =>
  import("./AdminPortal").then((module) => ({ default: module.AdminPortal })),
);
const InvitationPage = lazy(() =>
  import("./InvitationPage").then((module) => ({
    default: module.InvitationPage,
  })),
);
function LocalizedLanding() {
  const { locale } = useParams();
  return isLocale(locale) ? (
    <LandingPage locale={locale} />
  ) : (
    <Navigate to={`/${defaultLocale}`} replace />
  );
}
function LocalizedAuth({
  mode,
}: {
  mode: "login" | "register" | "forgot" | "reset";
}) {
  const { locale } = useParams();
  return isLocale(locale) ? (
    <AuthPage locale={locale} mode={mode} />
  ) : (
    <Navigate
      to={`/${defaultLocale}/${mode === "forgot" ? "forgot-password" : mode === "reset" ? "reset-password" : mode}`}
      replace
    />
  );
}
function App() {
  return (
    <Routes>
      <Route path="/:locale" element={<LocalizedLanding />} />
      <Route path="/:locale/login" element={<LocalizedAuth mode="login" />} />
      <Route
        path="/:locale/register"
        element={<LocalizedAuth mode="register" />}
      />
      <Route
        path="/:locale/forgot-password"
        element={<LocalizedAuth mode="forgot" />}
      />
      <Route
        path="/:locale/reset-password"
        element={<LocalizedAuth mode="reset" />}
      />
      <Route path="/:locale/app" element={<LocalizedPortal />} />
      <Route path="/:locale/admin" element={<LocalizedAdmin />} />
      <Route
        path="/:locale/invitations/:token"
        element={<LocalizedInvitation />}
      />
      <Route path="*" element={<Navigate to={`/${defaultLocale}`} replace />} />
    </Routes>
  );
}
function LocalizedInvitation() {
  const { locale } = useParams();
  return isLocale(locale) ? (
    <InvitationPage locale={locale} />
  ) : (
    <Navigate to={`/${defaultLocale}`} replace />
  );
}
function LocalizedPortal() {
  const { locale } = useParams();
  return isLocale(locale) ? (
    <AppPortal locale={locale} />
  ) : (
    <Navigate to={`/${defaultLocale}/app`} replace />
  );
}
function LocalizedAdmin() {
  const { locale } = useParams();
  return isLocale(locale) ? (
    <AdminPortal locale={locale} />
  ) : (
    <Navigate to={`/${defaultLocale}/admin`} replace />
  );
}
const root = document.getElementById("root");
if (!root) throw new Error("Application root is missing");
const application = (
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<main className="portal-state" aria-busy="true" />}>
          <App />
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
if (root.hasChildNodes()) hydrateRoot(root, application);
else createRoot(root).render(application);
